import { useState } from "react";
import { ExternalLink, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { isAllowedUrl, autoLabel } from "../../lib/sources";

/**
 * Editable list of { url, label } sources shown as removable chips with a
 * clickable link. "+ Add source" lets a reviewer paste an allowlisted URL.
 */
export default function SourceChips({ sources = [], onChange, testid = "sources", disabled = false }) {
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");

  function removeAt(i) {
    onChange(sources.filter((_, idx) => idx !== i));
  }

  function add() {
    const u = url.trim();
    if (!isAllowedUrl(u)) {
      toast.error("Source must be an https link on an allowed domain (DailyMed, AAD, PubMed…).");
      return;
    }
    if (sources.some((s) => s.url === u)) {
      toast.message("That source is already added.");
      setUrl(""); setLabel("");
      return;
    }
    onChange([...sources, { url: u, label: autoLabel(u, label) }]);
    setUrl(""); setLabel(""); setAdding(false);
  }

  return (
    <div data-testid={`${testid}-wrap`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {sources.map((s, i) => (
          <span
            key={`${s.url}-${i}`}
            data-testid={`${testid}-chip-${i}`}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[11px] text-stone-700"
          >
            <a
              href={s.url} target="_blank" rel="noreferrer"
              className="inline-flex min-w-0 items-center gap-1 truncate hover:underline"
              data-testid={`${testid}-link-${i}`}
            >
              <span className="truncate">{s.label}</span>
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
            {!disabled && (
              <button
                type="button" onClick={() => removeAt(i)} aria-label="remove source"
                className="shrink-0 text-stone-400 hover:text-red-600"
                data-testid={`${testid}-remove-${i}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {sources.length === 0 && (
          <span className="text-[11px] text-stone-400" data-testid={`${testid}-empty`}>
            No sources yet
          </span>
        )}
        {!disabled && !adding && (
          <button
            type="button" onClick={() => setAdding(true)}
            data-testid={`${testid}-add-toggle`}
            className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-stone-300 px-2 py-0.5 text-[11px] text-stone-600 hover:border-stone-500"
          >
            <Plus className="h-3 w-3" /> Add source
          </button>
        )}
      </div>
      {adding && !disabled && (
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`${testid}-add-row`}>
          <Input
            value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https:// allowed source URL"
            className="h-8 min-w-[180px] flex-1 text-xs"
            data-testid={`${testid}-url-input`}
          />
          <Input
            value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (optional)"
            className="h-8 w-40 text-xs"
            data-testid={`${testid}-label-input`}
          />
          <Button size="sm" className="h-8" onClick={add} data-testid={`${testid}-add-confirm`}>
            Add
          </Button>
          <button
            type="button"
            className="text-[11px] text-stone-400 hover:text-stone-700"
            onClick={() => { setAdding(false); setUrl(""); setLabel(""); }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
